import React, { useContext, useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import { Badge, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { SButton, SContainer, SError, SFormControl } from "../../components/BasicComponents";
import AppNavbar from "../../components/Navbars/AppNavbar";
import { StudentContext } from "../../context/StudentContext";
import { isLectureLive } from "../../utils/lectureUtils";
import { SBadge, SHome } from "./Home.styles";
import { onUnenrollFromCourse } from "../../utils/Alert";
import Loading from "../../components/Loading/Loading";
import { te } from "../../utils/errorsTranslation";

const StudentHomepage = () => {
  const { t } = useTranslation("", { keyPrefix: "home.student" });
  const history = useHistory();

  const [showEnrollToCourse, setShowEnrollToCourse] = useState(false);

  const { courses, getFollowedCourses, getCourseInfo, loading } = useContext(StudentContext);

  useEffect(() => {
    localStorage.setItem("lastAccess", "/student");
    getFollowedCourses().then((courses) => {
      courses?.forEach((course) => getCourseInfo(course.uuid));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <SContainer fluid fill="true">
          <Row>
            <AppNavbar
              startButtons={[
                <SButton className="pe-3" key="switch_teacher" onClick={() => history.push("/teacher")}>
                  <i className="fas fa-graduation-cap pt-1 px-2" />
                  {t("switch")}
                </SButton>,
              ]}
            />
          </Row>
          <SHome>
            <Container className="pt-5">
              <Row className="d-flex flex-row align-items-center">
                <Row className="w-auto fs-1 align-items-center flex-grow-1 fw-bold">{t("enrollments")}</Row>
                <Row className="w-auto fs-1">
                  <i
                    className="w-auto fas fa-plus"
                    onClick={() => setShowEnrollToCourse(true)}
                    data-for="courses-tooltip"
                    data-tip={t("tooltip.enroll")}
                  />
                </Row>
                <hr />
              </Row>
              {courses && Object.values(courses).map((course) => <Course key={course.uuid} course={course} />)}
            </Container>
          </SHome>

          <EnrollToCourseModal show={showEnrollToCourse} onHide={() => setShowEnrollToCourse(false)} />
          <ReactTooltip id="courses-tooltip" type="info" />
        </SContainer>
      )}
    </>
  );
};

const Course = ({ course }) => {
  const { t } = useTranslation("", { keyPrefix: "home.student" });

  const { unenrollFromCourse } = useContext(StudentContext);
  const history = useHistory();

  const [expanded, setExpanded] = useState(true);

  return (
    <Row key={course.uuid} className="course-row">
      <div className="d-flex flex-row align-items-baseline justify-content-between">
        <div className="fs-2 flex-grow-1 d-flex flex-row">
          <span className="ml-2">{course.name}</span>
        </div>
        <div className="d-flex fs-5 justify-content-end">
          <i
            data-for="course-tooltip"
            data-tip={expanded ? t("tooltip.hide_lectures") : t("tooltip.show_lectures")}
            className={`fas fa-angle-${expanded ? "down" : "right"} m-1`}
            onClick={() => setExpanded((e) => !e)}
          />
          <i
            data-for="course-tooltip"
            data-tip={t("tooltip.unenroll")}
            className="fas fa-sign-out-alt m-1"
            onClick={() => onUnenrollFromCourse(course, unenrollFromCourse)}
          ></i>
        </div>
        <ReactTooltip id="course-tooltip" type="info" />
      </div>
      <hr />
      {expanded &&
        course.lectures &&
        Object.values(course.lectures).map((lecture, index) => (
          <React.Fragment key={index}>
            {lecture && (
              <Row className="lecture-row">
                <div className="d-flex flex-row align-items-baseline justify-content-between fs-4">
                  <div className="d-flex flex-row flex-grow-1">
                    {lecture.type === "live" ? (
                      <SBadge>
                        <i className="fas fa-clock me-1" />
                        {t("start_time", { date: new Date(lecture.startTime) })}
                      </SBadge>
                    ) : (
                      <SBadge className="text-uppercase">
                        <i className="fas fa-undo me-1" />
                        {t("flipped")}
                      </SBadge>
                    )}
                    <span>{lecture.name}</span>
                    {isLectureLive(lecture) && (
                      <Badge bg="danger" className="mb-1 mx-2 text-uppercase">
                        {t("live")}
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex fs-5 flex-grow-1 justify-content-end">
                    <i
                      className="fas fa-play m-2"
                      onClick={() => {
                        history.push(`/student/lecture/${lecture.uuid}`);
                      }}
                      data-for="lecture-tooltip"
                      data-tip={t("tooltip.go")}
                    />
                    <ReactTooltip id="lecture-tooltip" type="info" />
                  </div>
                </div>
                <hr />
              </Row>
            )}
          </React.Fragment>
        ))}
    </Row>
  );
};

const EnrollToCourseModal = (props) => {
  const { t } = useTranslation("", { keyPrefix: "home.student.modal" });

  const [courseCode, setCourseCode] = useState("");
  const { enrollInCourse, error } = useContext(StudentContext);

  const onEnroll = () => {
    enrollInCourse(courseCode).then((course) => {
      if (course) props.onHide();
    });
  };

  return (
    <Modal {...props}>
      <Modal.Header closeButton>
        <Modal.Title>{t("enroll")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SFormControl type="text" placeholder="Folder code" onChange={(e) => setCourseCode(e.target.value)} />
        {error && <SError className="mt-3">{te(error.message)}</SError>}
      </Modal.Body>
      <Modal.Footer>
        <SButton className="btn btn-secondary" onClick={() => props.onHide()}>
          {t("close")}
        </SButton>
        <SButton disabled={courseCode.length === 0} className="btn btn-primary" onClick={onEnroll}>
          {t("confirm")}
        </SButton>
      </Modal.Footer>
    </Modal>
  );
};

export default StudentHomepage;
